<script setup>
import i18next from 'i18next';
import moment from 'moment';
import { computed, onMounted, ref, watch } from 'vue';
import { fetchData } from '../../../fetchData.js';
import { formatCurrency, formatDateByRegion } from '../../../helpers.js';
import Dropdown from '../../Dropdown.vue';
import Loading from '../../miscellaneous/Loading.vue';
import PortfolioItemCard from '../../PortfolioItemCard.vue';
import ScrollMoreButton from '../../ScrollMoreButton.vue';
import NoItemFound from '../noItemFound.vue';

const props = defineProps({
    heading: {
        type: String,
        required: true
    },

    editMode: {
        type: Boolean,
        required: true,
        default: false
    }
});
const searchPhrase = ref('');
const portfolioType = ref('buy_and_hold');

const newTrades = ref([]);
const status = ref('');
const statusValues = computed(() => {
    if (portfolioType.value === 'buy_and_hold') {
        return [
            { key: 'open', title: i18next.t('dashboard_portfolio_filter_states_active') },
            { key: 'closed', title: i18next.t('dashboard_portfolio_filter_states_finished') }
        ];
    }

    return [
        { key: 'open', title: i18next.t('dashboard_portfolio_filter_states_active_trade') },
        { key: 'closed', title: i18next.t('dashboard_portfolio_filter_states_finished_trade') }
    ];
});

const loading = ref(true);

watch(portfolioType, async () => {
    searchPhrase.value = '';
    status.value = '';
    loading.value = true;
    await updateTradesTable();
});

onMounted(async () => {
    await updateTradesTable();
});

const clearSearch = () => {
    searchPhrase.value = '';

    updateTradesTable();
};

const isAllSelected = (value) => value === null || value === 'Alle' || value === 'All';
const handleDropdownStatusChange = (value) => {
    if (isAllSelected(value)) {
        status.value = '';

        return updateTradesTable();
    }

    status.value = value;
    updateTradesTable();
};
const debounce = (func, delay) => {
    let timeout;

    return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => func(...args), delay);
    };
};
const handleSearchInput = debounce((value) => {
    searchPhrase.value = value;

    updateTradesTable();
}, 300);

const updateTradesTable = async () => {
    const API = `/api/v1/trades`;
    let url = `${API}?sort[by]=entry_time&sort[order]=desc&subscribed_abos=true&tradeType=${portfolioType.value}&page_size=20`;

    if (searchPhrase.value) {
        url += `&search_phrase=${encodeURIComponent(searchPhrase.value)}`;
    }

    if (status.value) {
        url += `&show_by_status[]=${encodeURIComponent(status.value)}`;
    }

    const { result = [] } = await fetchData(url);

    newTrades.value = [];

    result?.data?.forEach((trade, index) => {
        newTrades.value.push({
            id: trade.market_symbol_id,
            number: index,
            market: trade.market_symbol.name,
            logo: trade.market_symbol.logo,
            currency: trade.market_symbol.currency,
            direction: trade.direction,
            entry: trade.entry_price ? formatCurrency(trade.entry_price) : '-',
            entry_date: trade.entry_time ? formatDateByRegion(moment(trade.entry_time)) : null,
            entry_time: trade.entry_time
                ? new Intl.DateTimeFormat(navigator.language, {
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: undefined // Automatic adjustment to the user's system (24h or 12h -> AM/PM)
                  }).format(new Date(trade.entry_time))
                : null,
            entry_date_time: trade.entry_time ? moment(trade.entry_time) : null,
            updated_at: trade.updated_at ? trade.updated_at : trade.entry_time,
            exit: trade.exit_price ? formatCurrency(trade.exit_price) : '-',
            exit_date: trade.exit_time ? formatDateByRegion(moment(trade.exit_time)) : null,
            exit_time: trade.exit_time
                ? new Intl.DateTimeFormat(navigator.language, {
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: undefined // Automatic adjustment to the user's system (24h or 12h -> AM/PM)
                  }).format(new Date(trade.exit_time))
                : null,
            exit_date_time: trade.exit_time ? moment(trade.exit_time) : null,
            stop: trade.stop_loss ? formatCurrency(trade.stop_loss) : '-',
            current: trade.market_symbol.last_price ? formatCurrency(trade.market_symbol.last_price) : '-',
            performance: calculatePerformance(trade),
            link: `dashboard/markets-analysis/${trade?.recent_contentstream}`
        });
    });
    loading.value = false;
};

const calculatePerformance = (trade) => {
    if (!trade.entry_price) return '-';

    let performance = '-';
    const exitPrice = trade.exit_price || trade.market_symbol.last_price;

    if (exitPrice) {
        const directionMultiplier = trade.direction === 'Short' ? -1 : 1;
        const performanceValue = directionMultiplier * ((exitPrice - trade.entry_price) / trade.entry_price) * 100;
        performance = formatCurrency(performanceValue.toFixed(2)) + '%';

        if (parseFloat(performance.replace(',', '.')) > 0) {
            performance = '+' + performance;
        }
    }

    return performance;
};
</script>

<template>
    <div class="tourStep-3">
        <div class="flex flex-col pb-2 flex-1">
            <div class="rounded-xl bg-white flex flex-col gap-4 h-[600px]">
                <!-- Header section -->
                <div class="flex justify-between items-center gap-2 px-6 pt-6 pb-0">
                    <div class="flex items-center gap-2">
                        <p class="text-base font-bold">{{ props.heading }}</p>
                        <info-text v-if="!props.editMode" :title="props.heading" :text="$t('dashboard_portfolio_Info')" />
                    </div>
                    <RouterLink
                        :to="portfolioType == 'buy_and_hold' ? 'dashboard/trades' : 'dashboard/trading-depot'"
                        class="text-sm text-gray-500 hover:text-blue-dark flex items-center justify-center gap-1 border border-gray-300 hover:border-blue-dark px-3 py-1 rounded-lg transition duration-300 h-full"
                        style="align-items: center"
                    >
                        <p class="flex items-center h-full">
                            {{ portfolioType === 'buy_and_hold' ? $t('dashboard_portfolio_filter_states_all') : $t('dashboard_portfolio_filter_states_all_trades') }}
                        </p>
                        <i class="fi fi-rr-arrow-right text-xs flex items-center h-full ml-2"></i>
                    </RouterLink>
                </div>

                <!-- Portfolio Type Switch -->
                <div class="px-6">
                    <div class="flex items-center p-1 bg-gray-100 rounded-lg">
                        <button
                            :class="[
                                'w-1/2 py-1.5 text-sm font-semibold rounded-md transition-all duration-200',
                                portfolioType === 'buy_and_hold' ? 'bg-white text-blue-dark shadow' : 'text-gray-500 hover:bg-gray-200'
                            ]"
                            @click="portfolioType = 'buy_and_hold'"
                        >
                            {{ $t('nav_long_term_trades') }}
                        </button>
                        <button
                            :class="[
                                'w-1/2 py-1.5 text-sm font-semibold rounded-md transition-all duration-200',
                                portfolioType === 'trade' ? 'bg-white text-blue-dark shadow' : 'text-gray-500 hover:bg-gray-200'
                            ]"
                            @click="portfolioType = 'trade'"
                        >
                            {{ $t('nav_short_term_trades') }}
                        </button>
                    </div>
                </div>

                <!-- Search and Filter -->
                <div class="w-full px-6">
                    <div class="grid md:space-x-2 md:space-y-0 space-y-2 grid-cols-1 md:grid-cols-2">
                        <!-- Search Field -->
                        <div class="relative flex-1">
                            <div class="relative w-full lg:max-w-[370px]">
                                <input
                                    v-model="searchPhrase"
                                    type="text"
                                    :placeholder="$t('plain_search')"
                                    class="block w-full px-3 py-2 border rounded-lg bg-dashboard-primary shadow-sm hover:shadow-md focus:outline-none text-xs duration-300"
                                    @input="handleSearchInput($event.target.value)"
                                />
                                <i v-if="!searchPhrase" class="fi fi-rr-search absolute top-1/2 right-3 transform -translate-y-1/2 text-gray-400"></i>
                                <i
                                    v-else
                                    class="fi fi-rr-cross-circle absolute top-1/2 right-3 transform -translate-y-1/2 cursor-pointer hover:text-gray-600"
                                    @click="clearSearch"
                                ></i>
                            </div>
                        </div>
                        <!-- Filter Dropdown -->
                        <Dropdown
                            :dropdown-values="statusValues"
                            :selected-dropdown-value="status"
                            :filter-label="portfolioType === 'buy_and_hold' ? $t('dashboard_portfolio_filter_states_all') : $t('dashboard_portfolio_filter_states_all_trades')"
                            class="w-full"
                            @update:selected-dropdown-value="handleDropdownStatusChange"
                        />
                    </div>
                </div>
                <!-- Card List of Trades -->
                <div v-if="newTrades" class="relative flex flex-col px-6 mt-4 mb-6 text-xxs w-full overflow-y-scroll">
                    <div
                        ref="tradesScrollRef"
                        data-scroll-container
                        class="flex flex-col gap-3 h-full scroll-smooth pb-14"
                        :class="props.editMode ? 'overflow-hidden pointer-events-none' : 'overflow-y-auto'"
                    >
                        <div v-for="(row, index) in newTrades" :key="index">
                            <PortfolioItemCard :row="row" />
                        </div>

                        <div v-if="newTrades.length === 0 && !props.editMode">
                            <Loading :loading="loading">
                                <no-item-found class="h-[370px]" :item-name="$t('plain_trades')" />
                            </Loading>
                        </div>
                    </div>

                    <ScrollMoreButton v-if="!props.editMode && newTrades.length > 2" />
                </div>
            </div>
        </div>
    </div>
</template>
